import type { ICreateRoomCmd } from "@/data/commands/room/CreateRoomCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { RoomShape } from "@/data/model/Enums";
import type { IRoomWithService } from "@/data/model/Room";
import { Misc } from "@/data/util/Misc";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { TimePicker } from "@/portal/formControls/TimePicker";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class RoomForm extends BaseEntityForm<IRoomWithService, ICreateRoomCmd> {
    private $nameField: JQuery;
    private shapeSelect: Select<RoomShape>;
    private deallocDelayInput: TimePicker;
    private isAutomaticCheckoutEnabledCheck: Switch;
    private isActiveCheck: Switch;
    private $orderField: JQuery;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(RoomDao));

        this.$nameField = this.$form
            .find('.form-control[name="name"]')
            .change(() => this.updateName(this.$nameField.val()));

        const shapeSelect = this.$form.find('.form-control[name="shape"]');
        Misc.eachEnum(RoomShape, (i, e) => {
            shapeSelect.append('<option value="' + i + '">' + i18n("enums.RoomShape." + e) + "</option>");
        });

        this.shapeSelect = Select.createFromSelect(shapeSelect);
        this.deallocDelayInput = TimePicker.createFromInput(this.$form.find('.form-control[name="deallocationdelay"]'));

        this.isAutomaticCheckoutEnabledCheck = new Switch(this.$form.find('input[name="isautomaticcheckoutenabled"]'));
        this.isAutomaticCheckoutEnabledCheck.onChange = (value) => this.deallocDelayInput.setDisabled(!value);

        this.$orderField = this.$form.find('.form-control[name="order"]')["TouchSpin"]({
            min: 1,
            max: 10000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.isActiveCheck = new Switch(this.$form.find('input[name="isactive"]'));
    }

    public setFormData(model?: IRoomWithService) {
        this.updateName(model === undefined ? "" : model.name);
        this.$nameField.val(model === undefined ? "" : model.name);
        this.shapeSelect.setValue(model === undefined ? RoomShape.Landscape : model.shape);
        const isAutomaticCheckoutEnabled = model === undefined ? true : model.isAutomaticCheckoutEnabled;
        this.isAutomaticCheckoutEnabledCheck.setValue(isAutomaticCheckoutEnabled);
        this.deallocDelayInput.setValue(model === undefined ? undefined : model.deallocationDelay);
        this.deallocDelayInput.setDisabled(!isAutomaticCheckoutEnabled);
        this.isActiveCheck.setValue(model === undefined ? true : model.isActive);
        this.$orderField.val(model === undefined ? undefined : (model.order as any));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateRoomCmd {
        const data = {
            isActive: this.isActiveCheck.getValue(),
            name: this.$nameField.val().length > 0 ? this.$nameField.val() : undefined,
            shape: parseInt(this.shapeSelect.getValue()!),
            order: this.$orderField.val().length !== 0 ? parseInt(this.$orderField.val()) : undefined,
            deallocationDelay: this.deallocDelayInput.getValue(),
            isAutomaticCheckoutEnabled: this.isAutomaticCheckoutEnabledCheck.getValue(),
        };

        let hasError = false;
        this.deallocDelayInput.setError(false);

        if (data.name === undefined) {
            hasError = true;
            this.$nameField.addClass("is-invalid");
        }

        if (data.order !== undefined && isNaN(data.order)) {
            hasError = true;
            this.$orderField.addClass("is-invalid");
        }

        if (data.isAutomaticCheckoutEnabled && data.deallocationDelay !== undefined) {
            const s = this.deallocDelayInput.getSeconds();
            if (s! < 60) {
                hasError = true;
                this.deallocDelayInput.setError(true);
            }
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case "table_references":
                return i18n("room.failedTableReferences");
            case "room_limit_exceeded":
                return i18n("room.failedLimitExceeded");
            case "no_active_subscription":
                return i18n("modal.tableBulk.failedNoSubscription");
        }

        return undefined;
    }
}
